/**
 * A file ProGST sends itself, and saving it.
 *
 * Downloads used to follow a short-lived link to the object store. On UAT that address answered
 * with the FiveOX sign-in page, so every download and every *Show document* opened the sign-in
 * page instead of the file (2026-10-06). The bytes now come through ProGST's own API, signed in,
 * and the page saves or shows them itself, as *Download All (ZIP)* always did.
 */

export interface FetchedFile {
  blob: Blob
  /** The name to save it as (D-58), from `Content-Disposition`; null where none was sent. */
  filename: string | null
  /** The bytes' SHA-256 as the service recorded it. */
  sha256: string | null
  /** `pdf` | `image` | `none` — how a source document is shown, decided by the service. */
  render: string | null
}

/**
 * The name in a `Content-Disposition` header. `filename*` (UTF-8, RFC 5987) wins: a client's
 * legal name need not be ASCII, and the plain `filename` beside it is its ASCII fallback.
 */
export function fileNameFrom(disposition: string | null): string | null {
  if (!disposition) return null
  const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition)
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim())
    } catch {
      // A malformed encoding falls back to the plain name below.
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"/i.exec(disposition) ?? /filename\s*=\s*([^;]+)/i.exec(disposition)
  return plain?.[1]?.trim() || null
}

/** Save a fetched file under its own name, or `fallback` where the service sent none. */
export function saveFile(file: Pick<FetchedFile, 'blob' | 'filename'>, fallback: string): void {
  const url = URL.createObjectURL(file.blob)
  const link = document.createElement('a')
  link.href = url
  link.download = file.filename ?? fallback
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
